export type StatusCompositionVariant = 'hub' | 'context' | 'mobile'

export interface IStatusCompositionTokens {
  /** Outer width of the composition bar. Fixed per variant so bars align across list rows. */
  barWidth: number
  barHeight: number
  /** Vertical gap between the status label, the caption and the bar. */
  blockGap: string
}

export const STATUS_COMPOSITION_TOKENS: Record<StatusCompositionVariant, IStatusCompositionTokens> = {
  // `hub` stacks under the status label in a narrow tinted cell; `context` sits on the same line as
  // the status label, so its bar is kept short enough not to squeeze the context name column.
  hub: { barWidth: 88, barHeight: 4, blockGap: '6px' },
  context: { barWidth: 88, barHeight: 4, blockGap: '5px' },
  mobile: { barWidth: 92, barHeight: 4, blockGap: '4px' },
}

export const SEGMENT_GAP = 1.5
/** A segment never gets thinner than this, so a 1-of-20 bucket stays visible. */
export const MIN_SEGMENT_WIDTH = 4
/** Bar width inside the popover card (296px card minus its 14px padding). */
export const POPOVER_BAR_WIDTH = 268

export const CAPTION_FONT_SIZE = '11px'

/**
 * Hands the rounding remainder to the widest segments first, so the integer widths add up to
 * `target` exactly — otherwise the bar's fill would vary by a pixel between rows.
 */
const distributeRounding = (widths: number[], target: number): number[] => {
  const floored = widths.map((w) => Math.floor(w))
  let remainder = Math.round(target) - floored.reduce((sum, w) => sum + w, 0)

  // Widest first — the leftover pixel is least noticeable on a large segment.
  const byWidthDesc = widths.map((_, index) => index).sort((a, b) => widths[b] - widths[a])

  let cursor = 0

  while (remainder > 0 && byWidthDesc.length > 0) {
    floored[byWidthDesc[cursor % byWidthDesc.length]] += 1
    remainder -= 1
    cursor += 1
  }

  return floored
}

/**
 * Segment widths in **pixels** (not percentages): proportional to each bucket's share of `total`,
 * but never below `MIN_SEGMENT_WIDTH`. The pixels needed to lift thin segments up to the minimum
 * are taken from the segments above the minimum, proportionally to how much room they have to give.
 *
 * Handles the awkward cases the design brief calls out: `3/20 · 12/20 · 5/20` keeps the thin
 * segment visible, and five buckets at once still fit without anything collapsing to zero.
 */
export const computeSegmentWidths = (
  counts: number[],
  total: number,
  barWidth: number,
  gap: number = SEGMENT_GAP,
  minSegment: number = MIN_SEGMENT_WIDTH
): number[] => {
  const n = counts.length

  if (n === 0) return []

  const usable = barWidth - gap * (n - 1)

  if (usable <= 0) return counts.map(() => 0)

  // Too many buckets to honour the minimum — fall back to equal slices.
  if (usable < minSegment * n)
    return distributeRounding(
      counts.map(() => usable / n),
      usable
    )

  const denominator = total > 0 ? total : counts.reduce((sum, c) => sum + c, 0) || 1
  let widths = counts.map((count) => (count / denominator) * usable)

  const deficit = widths.reduce((sum, w) => sum + Math.max(0, minSegment - w), 0)
  const donors = widths.reduce((sum, w) => sum + Math.max(0, w - minSegment), 0)

  if (deficit > 0 && donors > 0) {
    widths = widths.map((w) => (w < minSegment ? minSegment : w - ((w - minSegment) / donors) * deficit))
  }

  return distributeRounding(widths, usable)
}
